import { useEffect, useState } from 'react';
import axios from 'axios';
import { Wallet, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VASettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [va, setVa] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');

  useEffect(() => {
    fetchVAProfile();
  }, []);

  const fetchVAProfile = async () => {
    try {
      // Get current user's VA profile directly
      const response = await axios.get(`${API_URL}/vas/me`);
      setVa(response.data);
      setWalletAddress(response.data.wallet_address || '');
    } catch (error) {
      console.error('Error fetching VA profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWallet = async (e) => {
    e.preventDefault();
    if (!va) return;

    setSaving(true);
    try {
      await axios.put(`${API_URL}/vas/${va.va_id}/wallet`, {
        wallet_address: walletAddress || null
      });
      toast.success('Wallet address updated');
      setVa({ ...va, wallet_address: walletAddress });
    } catch (error) {
      console.error('Error updating wallet:', error);
      toast.error(error.response?.data?.detail || 'Failed to update wallet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="va-settings-title">
          Settings
        </h1>
        <p className="text-base text-muted-foreground mt-2">
          Manage your account settings and payment information
        </p>
      </div>

      {/* Profile Info */}
      <div className="bg-card p-6 rounded-md border border-border">
        <h2 className="text-lg font-semibold text-white mb-4">Profile Information</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-zinc-500 uppercase mb-1">Name</div>
            <div className="text-white">{va?.name || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500 uppercase mb-1">Email</div>
            <div className="text-white">{va?.email || user?.email}</div>
          </div>
        </div>
      </div>

      {/* Wallet Settings */}
      <div className="bg-card p-6 rounded-md border border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-500/10 rounded-md">
            <Wallet className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Payment Wallet</h2>
            <p className="text-sm text-zinc-400">Your Solana wallet address for USDC payments</p>
          </div>
        </div>

        <form onSubmit={handleSaveWallet} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="wallet-address" className="text-white">Wallet Address</Label>
            <Input
              id="wallet-address"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              className="bg-zinc-900/50 border-zinc-800 text-white font-mono text-sm"
              placeholder="Your Solana wallet address"
              data-testid="wallet-address-input"
            />
            <p className="text-xs text-zinc-500">
              Enter your Solana wallet address to receive USDC payments. Make sure this is correct!
            </p>
          </div>

          {va?.wallet_address && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-md p-3">
              <div className="text-xs text-green-500 uppercase mb-1">Current Wallet</div>
              <div className="text-green-400 font-mono text-sm truncate">{va.wallet_address}</div>
            </div>
          )}

          <Button
            type="submit"
            disabled={saving}
            className="bg-white text-black hover:bg-zinc-200"
            data-testid="save-wallet-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Wallet Address'}
          </Button>
        </form>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-md p-4">
        <h3 className="text-blue-400 font-medium mb-2">Payment Information</h3>
        <ul className="text-sm text-blue-300 space-y-1">
          <li>• Payments are made in USDC on the Solana network</li>
          <li>• Make sure your wallet supports Solana USDC (SPL Token)</li>
          <li>• Payments are processed weekly based on your T1 clicks</li>
        </ul>
      </div>
    </div>
  );
}
